import React, { useRef, useEffect, useState, useCallback } from 'react';
import maplibregl, { Map, MapMouseEvent, MapTouchEvent, LngLat, RequestParameters, ResourceType } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import MapStyles from '../../MapStyles';
import { Plus, Minus } from 'lucide-react';

interface PMTilesMapProps {
    startingLocation: [number, number];
}

const PMTilesMap: React.FC<PMTilesMapProps> = ({ startingLocation }) => {
    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<Map | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isSourceLoaded, setIsSourceLoaded] = useState(false);

    // Kinetic scrolling state
    const isDragging = useRef<boolean>(false);
    const lastPos = useRef<{ x: number; y: number } | null>(null);
    const velocity = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const lastTime = useRef<number | null>(null);
    const moveThreshold = 1;
    const decelerationRate = 0.92;
    const velocityMultiplier = 0.004;

    const zoomTimeout = useRef<NodeJS.Timeout | null>(null);

    // Define your home location coordinates
    const startingLngLat = useRef<LngLat>(new LngLat(startingLocation[0], startingLocation[1]));
    const MAX_PAN_DISTANCE = 8000; // 6km in meters
    const EARTH_RADIUS = 6371000; // Earth's radius in meters
    const MAX_PAN_DISTANCE_SQUARED = MAX_PAN_DISTANCE * MAX_PAN_DISTANCE;
    const isAdjusting = useRef(false);

    const createEmptyPBFDataURL = (): string => {
        const emptyArrayBuffer = new ArrayBuffer(0);
        const blob = new Blob([emptyArrayBuffer], { type: 'application/x-protobuf' });
        return URL.createObjectURL(blob);
    };

    // Cache for the empty PBF data URL
    let emptyPBFDataURL: string | null = null;

    // Cache for checked font URLs
    const checkedFonts: { [url: string]: boolean } = {};

    // Function to check if a font file exists (synchronous version)
    const checkFontExistsSync = (url: string): boolean => {
        try {
            const xhr = new XMLHttpRequest();
            xhr.open('HEAD', url, false);  // false makes it synchronous
            xhr.send();
            return xhr.status !== 404;
        } catch (error) {
            console.error('Error checking font:', error);
            return false;
        }
    };

    // Function to check if a font file exists (asynchronous version for updating cache)
    const checkFontExistsAsync = (url: string): Promise<boolean> => {
        return fetch(url, { method: 'HEAD' })
            .then(response => response.ok)
            .catch(() => false);
    };

    useEffect(() => {
        if (map.current) return;

        try {
            if (!mapContainer.current) return;

            map.current = new maplibregl.Map({
                container: mapContainer.current,
                style: {
                    version: 8,
                    sources: {
                        'protomaps': {
                            type: 'vector',
                            tiles: [`https://map-tiles-worker.stwrtcnnr.workers.dev/australia/{z}/{x}/{y}.mvt`],
                            minzoom: 0,
                            maxzoom: 15
                        },
                    },
                    glyphs: `${window.location.origin}/fonts/{fontstack}/{range}.pbf`,
                    layers: MapStyles,
                },
                center: [startingLocation[0], startingLocation[1]],
                zoom: 14.0,
                scrollZoom: false,
                doubleClickZoom: false,
                dragRotate: false,
                touchZoomRotate: false,
                transformRequest: (url: string, resourceType?: ResourceType): RequestParameters => {
                    console.debug(`Requesting ${resourceType}: ${url}`);

                    if (resourceType === 'Glyphs') {
                        if (checkedFonts[url] === undefined) {
                            // Perform synchronous check
                            const exists = checkFontExistsSync(url);
                            checkedFonts[url] = exists;

                            // Update cache asynchronously for future requests
                            checkFontExistsAsync(url).then(asyncExists => {
                                checkedFonts[url] = asyncExists;
                            });
                        }

                        if (!checkedFonts[url]) {
                            console.warn(`Font not found: ${url}`);
                            if (!emptyPBFDataURL) {
                                emptyPBFDataURL = createEmptyPBFDataURL();
                            }
                            return {
                                url: emptyPBFDataURL,
                                credentials: 'same-origin',
                                collectResourceTiming: false,
                            };
                        }
                    }
                    return { url };
                },
            });

            map.current.on('load', () => {
                console.log('Map loaded successfully');

                if (!map.current) return;

                // Add a new source for the home location point
                map.current.addSource('starting-location', {
                    type: 'geojson',
                    data: {
                        type: 'Feature',
                        properties: {},
                        geometry: {
                            type: 'Point',
                            coordinates: [startingLocation[0], startingLocation[1]]
                        }
                    }
                });

                // Add a circle layer with a fixed 100m radius
                map.current.addLayer({
                    id: 'starting-location-circle',
                    type: 'circle',
                    source: 'starting-location',
                    paint: {
                        'circle-radius': [
                            'interpolate',
                            ['exponential', 2],
                            ['zoom'],
                            0, 0,
                            20, 100 * Math.pow(2, 20 - 15)  // 100 meters at zoom level 15
                        ],
                        'circle-color': 'grey',
                        'circle-opacity': 0.6
                    }
                });

                // Add a symbol layer for the text
                map.current.addLayer({
                    id: 'starting-location-text',
                    type: 'symbol',
                    source: 'starting-location',
                    minzoom: 10,  // Text layer will only be visible at zoom level 10 and above
                    layout: {
                        'text-field': ['format',
                            //'Home', { 'font-scale': 1.2 },
                            '\n', {},
                            //'Location', { 'font-scale': 1.2 }
                        ],
                        'text-font': ['Inter'],
                        'text-size': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            13, 4,  // Minimum text size
                            15, 20   // Maximum text size
                        ],
                        'text-anchor': 'center',
                        'text-justify': 'center',
                        'text-max-width': 8
                    },
                    paint: {
                        'text-color': 'white',
                        'text-opacity': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            10, 0,    // Fully transparent at zoom level 10
                            10.5, 1   // Fully opaque at zoom level 10.5
                        ]
                    }
                });

                console.log('Map zoom range:', map.current.getMinZoom(), '-', map.current.getMaxZoom());
                const bounds = map.current.getBounds();
                console.log('Map bounds:', bounds.toString());
                const source = map.current.getSource('protomaps');
                if (source && 'vectorLayerIds' in source) {
                    console.log('Available source layers:', source.vectorLayerIds);
                }

                map.current.on('mousedown', handleTouchStart);
                map.current.on('touchstart', handleTouchStart);
                map.current.on('mousemove', handleTouchMove);
                map.current.on('touchmove', handleTouchMove);
                map.current.on('mouseup', handleTouchEnd);
                map.current.on('touchend', handleTouchEnd);
                map.current.on('mouseout', stopDragging);
                map.current.on('dblclick', handleDoubleClick);
                map.current.on('moveend', checkBounds);
            });

            // Add source loading error handling
            map.current.on('sourcedata', (e) => {
                if (e.isSourceLoaded && e.sourceId === 'protomaps') {
                    setIsSourceLoaded(true);
                    console.debug('Map source loaded successfully', {
                        sourceId: e.sourceId,
                        dataType: e.dataType
                    });
                }
            });

            map.current.on('data', (e) => {
                if (e.dataType === 'source' && e.tile) {
                    console.debug('Tile loaded:', {
                        tileId: e.tile.tileID,
                        state: e.tile.state,
                        timings: performance.getEntriesByName(e.tile.actor?.target?.url)?.[0]
                    });
                }
            });

            map.current.on('error', (e) => {
                console.error('MapLibre GL error:', {
                    error: e.error,
                    sourceId: e.sourceId,
                    timestamp: new Date().toISOString(),
                    tileId: e.tile?.tileID,
                    originalUrl: e.tile?.actor?.target?.url,
                    status: e.status
                });
                
                if (e.error?.message?.includes('Unimplemented type: 4')) {
                    // Just log for this specific error since it might be transient
                    console.warn('Invalid geometry type in tile:', {
                        tile: e.tile?.tileID,
                        url: e.tile?.actor?.target?.url
                    });
                    return;
                }

                setError('An error occurred while loading the map data.');
            });

        } catch (e) {
            console.error('Error initializing map:', e);
            setError('An error occurred while initializing the map.');
        }

        return () => {
            map.current?.remove();
        };
    }, []);

    const handleTouchStart = (e: MapMouseEvent | MapTouchEvent) => {
        if (!map.current) return;
        e.preventDefault(); // Prevent default touch behavior
        isDragging.current = true;
        lastPos.current = getEventPosition(e);
        lastTime.current = Date.now();
        velocity.current = { x: 0, y: 0 };
    };

    const handleTouchMove = (e: MapMouseEvent | MapTouchEvent) => {
        if (!isDragging.current || !map.current || !lastPos.current || !lastTime.current) return;
        e.preventDefault(); // Prevent default touch behavior

        const pos = getEventPosition(e);
        if (!pos) return;

        const dx = lastPos.current.x - pos.x;
        const dy = lastPos.current.y - pos.y;
        const now = Date.now();
        const dt = now - lastTime.current;

        if (dt > 0) {
            velocity.current = {
                x: dx / dt * 1000,
                y: dy / dt * 1000
            };
        }

        map.current.panBy([dx, dy], { animate: false });
        checkBounds();
        lastPos.current = pos;
        lastTime.current = now;
    };

    const handleTouchEnd = (e: MapMouseEvent | MapTouchEvent) => {
        e.preventDefault(); // Prevent default touch behavior
        stopDragging();
    };

    const stopDragging = () => {
        if (!isDragging.current || !map.current) return;

        isDragging.current = false;
        map.current.getCanvas().style.cursor = '';

        if (Math.abs(velocity.current.x) > moveThreshold || Math.abs(velocity.current.y) > moveThreshold) {
            kineticScroll();
        }
    };
    const calculateDistanceSquared = useCallback((point1: LngLat, point2: LngLat): number => {
        const dx = (point1.lng - point2.lng) * Math.cos((point1.lat + point2.lat) * 0.5 * Math.PI / 180);
        const dy = point1.lat - point2.lat;
        return 111319.9 * 111319.9 * (dx * dx + dy * dy);
    }, []);

    const calculateBearing = useCallback((start: LngLat, end: LngLat): number => {
        const startLat = start.lat * Math.PI / 180;
        const startLng = start.lng * Math.PI / 180;
        const endLat = end.lat * Math.PI / 180;
        const endLng = end.lng * Math.PI / 180;

        const y = Math.sin(endLng - startLng) * Math.cos(endLat);
        const x = Math.cos(startLat) * Math.sin(endLat) -
            Math.sin(startLat) * Math.cos(endLat) * Math.cos(endLng - startLng);
        const bearing = Math.atan2(y, x);
        return (bearing * 180 / Math.PI + 360) % 360;
    }, []);

    const calculateOffset = useCallback((start: LngLat, distance: number, bearing: number): LngLat => {
        const R = 6371000; // Earth's radius in meters
        const d = distance / R;
        const bearingRad = bearing * Math.PI / 180;

        const lat1 = start.lat * Math.PI / 180;
        const lon1 = start.lng * Math.PI / 180;

        const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) +
            Math.cos(lat1) * Math.sin(d) * Math.cos(bearingRad));
        const lon2 = lon1 + Math.atan2(Math.sin(bearingRad) * Math.sin(d) * Math.cos(lat1),
            Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));

        return new LngLat((lon2 * 180 / Math.PI + 540) % 360 - 180, lat2 * 180 / Math.PI);
    }, []);

    const checkBounds = useCallback(() => {
        if (!map.current || isAdjusting.current) return;

        const center = map.current.getCenter();
        const distanceSquared = calculateDistanceSquared(center, startingLngLat.current);

        if (distanceSquared > MAX_PAN_DISTANCE_SQUARED) {
            isAdjusting.current = true;
            const bearing = calculateBearing(startingLngLat.current, center);
            const limitedCenter = calculateOffset(startingLngLat.current, MAX_PAN_DISTANCE, bearing);
            map.current.setCenter(limitedCenter);
            isAdjusting.current = false;
        }
    }, [calculateDistanceSquared, calculateBearing, calculateOffset]);


    const kineticScroll = () => {
        if (!map.current) return;

        let vx = velocity.current.x * velocityMultiplier;
        let vy = velocity.current.y * velocityMultiplier;

        const animate = () => {
            if (!map.current || (Math.abs(vx) < 0.1 && Math.abs(vy) < 0.1)) return;

            map.current.panBy([vx, vy], { animate: false });
            checkBounds();
            vx *= decelerationRate;
            vy *= decelerationRate;

            requestAnimationFrame(animate);
        };

        animate();
    };

    const getEventPosition = (e: MapMouseEvent | MapTouchEvent): { x: number; y: number } | null => {
        if ('point' in e) {
            // This is a MapMouseEvent
            return e.point;
        }
        return null; // Return null if we can't get a position
    };

    const handleZoom = (direction: 'in' | 'out') => {
        if (map.current) {
            const zoom = map.current.getZoom();
            const newZoom = direction === 'in' ? zoom + 1 : zoom - 1;
            setZoom(newZoom);
        }
    };

    const handleDoubleClick = (e: MapMouseEvent) => {
        if (map.current) {
            e.preventDefault();
            const currentZoom = map.current.getZoom();
            setZoom(currentZoom + 1, e.lngLat);
        }
    };

    const setZoom = (newZoom: number, center?: maplibregl.LngLat) => {
        if (map.current) {
            const clampedZoom = Math.min(Math.max(newZoom, 12.0), 17.0);
            map.current.easeTo({
                zoom: clampedZoom,
                center: center,
                duration: 300
            });
        }
    };

    // Very stupid, but onClick doesn't trigger while the panning animation is playing and a user TOUCHES the buttons.
    // Also need a time out to prevent double triggers since if the animation is not playing then both onClick and
    // onTouchEnd are called. I assume this workaround needs to happen because of my panning logic. Worth noting that
    // this workaround is required even if removing all the e.preventDefault()s are removed
    const handleZoomButtonInteraction = useCallback((direction: 'in' | 'out') => {
        if (zoomTimeout.current) {
            clearTimeout(zoomTimeout.current);
        }
        zoomTimeout.current = setTimeout(() => {
            handleZoom(direction);
        }, 20);
    }, [handleZoom]);


    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div className="relative">
            <div ref={mapContainer} className="w-full h-[400px] rounded-xl" />
            <div className="absolute top-2 right-2 flex flex-col gap-2">
                <button
                    onClick={() => handleZoomButtonInteraction('in')}
                    onTouchEnd={() => handleZoomButtonInteraction('in')}

                    className="bg-white p-2 rounded-full shadow-md"
                    aria-label="Zoom in"
                >
                    <Plus size={24} />
                </button>
                <button
                    onClick={() => handleZoomButtonInteraction('out')}
                    onTouchEnd={() => handleZoomButtonInteraction('out')}
                    className="bg-white p-2 rounded-full shadow-md"
                    aria-label="Zoom out"
                >
                    <Minus size={24} />
                </button>
            </div>
        </div>
    );
};

export default PMTilesMap;